import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

/**
 * FingerprintJS visitor id, hashed once for `X-Device-Fingerprint`.
 * Falls back to a coarse hash if `get()` fails.
 */
@Injectable({ providedIn: 'root' })
export class BrowserFingerprintService {
  private cached: string | null = null;
  private initPromise: Promise<string> | null = null;
  private agentPromise: ReturnType<typeof FingerprintJS.load> | null = null;

  async initialize(): Promise<string> {
    if (this.cached) {
      return this.cached;
    }
    if (!this.initPromise) {
      this.initPromise = this.resolveFingerprint().then((id) => {
        this.cached = id;
        return id;
      });
    }
    return this.initPromise;
  }

  getCached(): string | null {
    return this.cached;
  }

  /** Clear cache and recompute before login/handoff API calls. */
  async setContextForLogin(): Promise<string> {
    this.cached = null;
    this.initPromise = null;
    return this.initialize();
  }

  private agent(): ReturnType<typeof FingerprintJS.load> {
    if (!this.agentPromise) {
      this.agentPromise = FingerprintJS.load();
    }
    return this.agentPromise;
  }

  private async resolveFingerprint(): Promise<string> {
    try {
      const fp = await this.agent();
      const { visitorId } = await fp.get();
      return await this.toSha256Hex(visitorId);
    } catch {
      return this.fallbackFingerprint();
    }
  }

  private async toSha256Hex(value: string): Promise<string> {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private async fallbackFingerprint(): Promise<string> {
    const rough = [
      navigator.userAgent ?? '',
      typeof screen !== 'undefined' ? `${screen.width}x${screen.height}x${screen.colorDepth}` : '',
      navigator.language ?? '',
      String(new Date().getTimezoneOffset()),
      window.devicePixelRatio != null ? String(window.devicePixelRatio) : '',
    ].join('|');
    return this.toSha256Hex(rough);
  }
}
